import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiData, MoreMenuClickBiEvent, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import VerticalEllipsis from '@wix/da-ds/pkg/Icons/16x16/VerticalEllipsis';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';

export interface Props {
  deviation: PapiDeviation;
  items: NullableDropdownItem[];
  wrapperClassName?: string;
  className?: string;
  withIconBackground?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const ContextMenu: FC<Props> = ({
  deviation,
  items,
  wrapperClassName,
  className,
  withIconBackground,
  onOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const biData = BiData<MoreMenuClickBiEvent>({
    evid: 417,
    sectionname: BiContextProvidedValue,
    typeid: getDeviationResourceType(deviation),
    itemid: deviation.deviationId,
  });

  const contextMenu = isMobile ? (
    <IconButtonWithContextMenu
      icon={VerticalEllipsis}
      size="medium"
      variant="main"
      aria-label={t('siteHeader.more')}
      items={items}
      biData={biData}
    />
  ) : (
    <IconButtonWithContextMenu
      className={className}
      placement={'bottom-start'}
      items={items}
      size="medium"
      variant="main"
      icon={EllipsisIcon}
      aria-label={t('siteHeader.more')}
      onClick={e => e.stopPropagation()}
      title={t('common.more')}
      standardMenuProps={{ usePortal: true } as any}
      popperManagerProps={{ onOpen, onClose }}
      withBackground={withIconBackground}
      biData={biData}
    />
  );

  return (
    <BiLoggerContextProvider value={{ sectionname: 'studio-menu' }}>
      <div className={wrapperClassName}>
        {/* We need this wrapper div to avoid a layout shift on render */}
        {contextMenu}
      </div>
    </BiLoggerContextProvider>
  );
};
ContextMenu.displayName = 'ContextMenu';

export default ContextMenu;
